import { Suspense, lazy, type ComponentType } from 'react';
import { createHashRouter, Navigate, useNavigate } from 'react-router-dom';
import { StrategistLoadingState } from './components/StrategistLoadingState';
import { Layout } from './layouts/Layout';

function lazyPage<T extends ComponentType<any>>(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[exportName] as T };
  });
}

const Dashboard = lazyPage(() => import('./pages/Dashboard'), 'Dashboard');
const Calendar = lazyPage(() => import('./pages/Calendar'), 'Calendar');
const Races = lazyPage(() => import('./pages/Races'), 'Races');
const Standings = lazyPage(() => import('./pages/Standings'), 'Standings');
const Drivers = lazyPage(() => import('./pages/Drivers'), 'Drivers');
const DriverDetail = lazyPage(() => import('./pages/DriverDetail'), 'DriverDetail');
const Constructors = lazyPage(() => import('./pages/Constructors'), 'Constructors');
const ConstructorDetail = lazyPage(() => import('./pages/ConstructorDetail'), 'ConstructorDetail');
const Circuits = lazyPage(() => import('./pages/Circuits'), 'Circuits');
const CircuitDetail = lazyPage(() => import('./pages/CircuitDetail'), 'CircuitDetail');
const HeadToHead = lazyPage(() => import('./pages/HeadToHead'), 'HeadToHead');
const TitleFights = lazyPage(() => import('./pages/TitleFights'), 'TitleFights');
const Eras = lazyPage(() => import('./pages/Eras'), 'Eras');
const LapTimes = lazyPage(() => import('./pages/LapTimes'), 'LapTimes');
const Records = lazyPage(() => import('./pages/Records'), 'Records');
const Analytics = lazyPage(() => import('./pages/Analytics'), 'Analytics');
const Methodology = lazyPage(() => import('./pages/Methodology'), 'Methodology');
const RacePredictor = lazyPage(() => import('./pages/RacePredictor'), 'RacePredictor');
const Championship = lazyPage(() => import('./pages/Championship'), 'Championship');
const News = lazyPage(() => import('./pages/News'), 'News');
const Polls = lazyPage(() => import('./pages/Polls'), 'Polls');
const Teammates = lazyPage(() => import('./pages/Teammates'), 'Teammates');
const WhatIf = lazyPage(() => import('./pages/WhatIf'), 'WhatIf');

function RouteLoading() {
  return (
    <StrategistLoadingState
      eyebrow="Loading"
      title="Opening page"
      subtitle="Getting the next page ready..."
    />
  );
}

function renderLazyRoute(Page: ComponentType) {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Page />
    </Suspense>
  );
}

function RouteError() {
  const navigate = useNavigate();
  return (
    <div role="alert" className="app-error-state">
      <div className="app-error-shell">
        <p className="app-error-eyebrow">Something went wrong</p>
        <p className="app-error-copy">
        This route could not be opened cleanly. Head back to the dashboard and try again.
        </p>
        <div className="app-error-actions">
          <button onClick={() => navigate('/dashboard')} className="app-error-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="app-error-state">
      <div className="app-error-shell app-error-shell-centered">
        <p className="app-error-code">404</p>
        <p className="app-error-copy">This page does not exist.</p>
        <div className="app-error-actions">
          <a href="#/dashboard" className="app-error-link">Back to Dashboard</a>
        </div>
      </div>
    </div>
  );
}

export const router = createHashRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: renderLazyRoute(Dashboard) },
      { path: 'calendar', element: renderLazyRoute(Calendar) },
      { path: 'calendar/:year', element: renderLazyRoute(Calendar) },
      { path: 'races', element: renderLazyRoute(Races) },
      { path: 'races/:year', element: renderLazyRoute(Races) },
      { path: 'races/:year/:round', element: renderLazyRoute(Races) },
      { path: 'standings', element: renderLazyRoute(Standings) },
      { path: 'standings/:year', element: renderLazyRoute(Standings) },
      { path: 'drivers', element: renderLazyRoute(Drivers) },
      { path: 'drivers/:id', element: renderLazyRoute(DriverDetail) },
      { path: 'drivers/:id/:year', element: renderLazyRoute(DriverDetail) },
      { path: 'constructors', element: renderLazyRoute(Constructors) },
      { path: 'constructors/:id', element: renderLazyRoute(ConstructorDetail) },
      { path: 'circuits', element: renderLazyRoute(Circuits) },
      { path: 'circuits/:id', element: renderLazyRoute(CircuitDetail) },
      { path: 'h2h', element: renderLazyRoute(HeadToHead) },
      { path: 'h2h/:driver1/:driver2/:from/:to', element: renderLazyRoute(HeadToHead) },
      { path: 'teammates', element: renderLazyRoute(Teammates) },
      { path: 'title-fights', element: renderLazyRoute(TitleFights) },
      { path: 'title-fights/:year', element: renderLazyRoute(TitleFights) },
      { path: 'eras', element: renderLazyRoute(Eras) },
      { path: 'eras/:era', element: renderLazyRoute(Eras) },
      { path: 'lap-times', element: renderLazyRoute(LapTimes) },
      { path: 'lap-times/:circuit', element: renderLazyRoute(LapTimes) },
      { path: 'records', element: renderLazyRoute(Records) },
      { path: 'analytics', element: renderLazyRoute(Analytics) },
      { path: 'methodology', element: renderLazyRoute(Methodology) },
      { path: 'predictions', element: renderLazyRoute(RacePredictor) },
      { path: 'championship', element: renderLazyRoute(Championship) },
      { path: 'what-if', element: renderLazyRoute(WhatIf) },
      { path: 'polls', element: renderLazyRoute(Polls) },
      { path: 'news', element: renderLazyRoute(News) },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
